import {
  cardHasChanges,
  getVersionCategoryCards,
  groupVersionCategoryCardsByRole,
  VersionCategoryCard,
} from "./categoryVersionDiff";
import {
  CategorizationMap,
  categoryRolesForCategories,
  PLANT_PROFILE_ROLES,
  toRadioChip,
} from "portal/utils/categoryCollection/category";
import { CategorizedImages } from "../categorized/CategorizedImages";
import { Category, Role } from "protos/robot_syncer/plant_profile";
import {
  Dimensions,
  imagesToCategoryIds,
} from "portal/utils/categoryCollection/image";
import { membershipRolesFromPairs } from "portal/utils/categoryCollection/plantProfile";
import { PlantProfileRoleSectionHeader } from "../PlantProfileRoleSectionHeader";
import { portalEndpoints } from "portal/state/portalApi";
import { ShootTimeHistorySection } from "./ShootTimeHistorySection";
import { Skeleton } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { titleCase } from "common/utils/strings";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useShootTimeExpanded } from "../useShootTimeExpanded";
import { useTranslation } from "react-i18next";
import { useVersionHistory } from "./VersionHistoryProvider";
import { VersionHeader } from "./VersionHeader";
import { VersionHistoryList } from "./VersionHistoryList";
import React, { FC, useMemo, useState } from "react";

interface PlantProfileHistoryProps {
  uuid: string;
  dimensions: Dimensions;
  onRevert?: (revertToVersionId: string) => Promise<unknown>;
  isReverting: boolean;
  currentVersionCategorizationMap: CategorizationMap;
  onClassify?: (
    predictionPointId?: string,
    categoryId?: string
  ) => void | Promise<unknown>;
}

export const PlantProfileHistory: FC<PlantProfileHistoryProps> = ({
  uuid,
  dimensions,
  onRevert,
  isReverting,
  currentVersionCategorizationMap,
  onClassify,
}) => {
  const { i18n, t } = useTranslation();
  const { selectedVersionId, refetchVersions } = useVersionHistory();

  const [onlyChanges, setOnlyChanges] = useState(false);

  const { currentData: selectedVersion, isFetching: isSelectedVersionLoading } =
    useQueryPopups(
      portalEndpoints.getPlantProfileVersion.useQuery(
        selectedVersionId
          ? {
              ccUuid: uuid,
              ccvUuid: selectedVersionId,
            }
          : skipToken
      )
    );

  const compareVersionId = selectedVersion?.metadata?.parentVersionId;

  const {
    currentData: compareWithVersion,
    isFetching: isCompareVersionLoading,
  } = useQueryPopups(
    portalEndpoints.getPlantProfileVersion.useQuery(
      compareVersionId
        ? {
            ccUuid: uuid,
            ccvUuid: compareVersionId,
          }
        : skipToken
    )
  );

  const categories = useMemo(() => {
    if (!selectedVersion) {
      return;
    }
    const cats: Category[] = [];
    for (const c of selectedVersion.profile?.categories ?? []) {
      cats.push(c.category!);
    }
    return cats;
  }, [selectedVersion]);
  const categorizationMap = useMemo(
    () => (categories ? imagesToCategoryIds(categories) : {}),
    [categories]
  );
  const categoryOptions = useMemo(
    () => (categories ? toRadioChip(i18n, t, categories, true) : []),
    [categories, i18n, t]
  );
  const categoryCards = useMemo(() => {
    const currentVersion = {
      categories: categories ?? [],
      options: categoryOptions,
      roles: categoryRolesForCategories(
        categories ?? [],
        membershipRolesFromPairs(selectedVersion?.profile?.categoryMemberships)
      ),
    };
    if (!compareWithVersion) {
      return getVersionCategoryCards(currentVersion);
    }
    const versusCategories: Category[] = [];
    for (const c of compareWithVersion.profile?.categories ?? []) {
      versusCategories.push(c.category!);
    }
    const versusVersion = {
      categories: versusCategories,
      options: toRadioChip(i18n, t, versusCategories, true),
      roles: categoryRolesForCategories(
        versusCategories,
        membershipRolesFromPairs(
          compareWithVersion.profile?.categoryMemberships
        )
      ),
    };
    return getVersionCategoryCards(currentVersion, versusVersion);
  }, [
    categories,
    categoryOptions,
    compareWithVersion,
    i18n,
    selectedVersion?.profile?.categoryMemberships,
    t,
  ]);
  const categoryCardsByRole = useMemo(() => {
    const byRole = groupVersionCategoryCardsByRole(categoryCards);
    if (!onlyChanges) {
      return byRole;
    }
    const changed = new Map<Role, VersionCategoryCard[]>();
    for (const [role, cards] of byRole) {
      changed.set(
        role,
        cards.filter((card) => cardHasChanges(card))
      );
    }
    return changed;
  }, [categoryCards, onlyChanges]);

  const cardsWithShootTime = [...categoryCardsByRole.values()]
    .flat()
    .filter(({ shootTime }) => shootTime);
  const {
    isShootTimeExpanded,
    allShootTimeExpanded,
    setAllShootTimeExpanded,
    onShootTimeExpandedChange,
  } = useShootTimeExpanded(
    cardsWithShootTime.map(({ categoryId }) => categoryId)
  );

  return (
    <div className="flex flex-col md:flex-row">
      <div className="w-full md:w-1/3 md:min-w-72 mb-4 md:mb-0">
        <div className="h-80 md:h-fit min-h-0 overflow-y-auto">
          <VersionHistoryList />
        </div>
      </div>

      <div className="w-full pl-2">
        {selectedVersion?.metadata && (
          <VersionHeader
            metadata={selectedVersion.metadata}
            onRevert={onRevert}
            isReverting={isReverting}
            onlyChanges={onlyChanges}
            onToggleOnlyChanges={setOnlyChanges}
            hasShootTime={cardsWithShootTime.length > 0}
            shootTimeExpanded={allShootTimeExpanded}
            onToggleShootTime={() => {
              setAllShootTimeExpanded(!allShootTimeExpanded);
            }}
          />
        )}
        <div className="w-full flex flex-col gap-4 mt-3">
          {isSelectedVersionLoading || isCompareVersionLoading
            ? Array.from({ length: 5 })
                .fill(undefined)
                .map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    className="w-full h-32"
                  />
                ))
            : PLANT_PROFILE_ROLES.map((sectionRole) => {
                const sectionCards = categoryCardsByRole.get(sectionRole) ?? [];
                if (sectionCards.length === 0) {
                  return;
                }
                return (
                  <div key={sectionRole} className="flex flex-col gap-2">
                    <PlantProfileRoleSectionHeader role={sectionRole} />
                    {sectionCards.map(
                      ({
                        categoryId,
                        name,
                        predictionPointIds,
                        versus,
                        shootTime,
                        role,
                      }) => (
                        <CategorizedImages
                          key={categoryId}
                          categoryId={categoryId}
                          categoryName={name}
                          categoryOptions={categoryOptions}
                          predictionPointIds={predictionPointIds}
                          dimensions={dimensions}
                          categorizationMap={categorizationMap}
                          versus={versus}
                          categoryRole={role}
                          onClassify={
                            onClassify
                              ? async (
                                  predictionPointId: string,
                                  nextCategoryId: string
                                ) => {
                                  await onClassify(
                                    predictionPointId,
                                    nextCategoryId
                                  );
                                  refetchVersions();
                                }
                              : undefined
                          }
                          classificationAction="revive"
                          rightClickMenuTitle={titleCase(
                            t(
                              "components.categoryCollectionProfile.actions.revive"
                            )
                          )}
                          isRightClickDisabled={(predictionPointId: string) =>
                            Boolean(
                              currentVersionCategorizationMap[predictionPointId]
                            )
                          }
                        >
                          {shootTime && (
                            <ShootTimeHistorySection
                              shootTime={shootTime}
                              expanded={isShootTimeExpanded(categoryId)}
                              onExpandedChange={(expanded) => {
                                onShootTimeExpandedChange(categoryId, expanded);
                              }}
                            />
                          )}
                        </CategorizedImages>
                      )
                    )}
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
};
